import {useInfiniteQuery} from '@tanstack/react-query'
import {useState} from 'react'
import api from 'src/services/api'
import {Lpr, LprFilterType} from 'src/services/api/endpoints/LprEndpoint'
import {QueryKeyEnum} from 'src/utilities/enums/QueryKeyEnum'
import {FaceDetectionWSChannelEnum, WebSocketRoomTypeEnum} from 'src/utilities/enums/WSChannelEnums'
import useWebsocket from '../socket/useWebsocket'

const BATCH_SIZE = 20

export type SentToFilter = {
  sentToHarpia?: boolean
  sentToSpia?: boolean
  sentToSivana?: boolean
}

type Props = {
  filters?: LprFilterType
  sentTo?: SentToFilter
  start?: string
  end?: string
  enabled: boolean
}

export default function useLicensePlate({filters, sentTo, start, end, enabled}: Props) {
  const [lprEvents, setLprEvents] = useState<Lpr[]>([])

  const onMessage = (event: Lpr) => {
    setLprEvents((prev) => {
      prev.push(event)
      const newFaceDetections = prev
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .filter((detection, index, self) => index === self.findIndex((t) => t.id === detection.id))
        .filter((detection) => filters?.companyIds?.includes(detection.camera.companyId)) // todo: this filter needs to be in the backend
        .filter((detection) => (start ? new Date(detection.createdAt).getTime() >= new Date(start).getTime() : true)) // todo: this filter needs to be in the backend
        .filter((detection) => (end ? new Date(detection.createdAt).getTime() <= new Date(end).getTime() : true)) // todo: this filter needs to be in the backend

      return newFaceDetections
    })
  }

  const websocket = useWebsocket({
    room: WebSocketRoomTypeEnum.LPR,
    listenTo: [FaceDetectionWSChannelEnum.LPR_DETECTION],
    onMessage,
    enable: enabled,
  })

  const query = useInfiniteQuery({
    retry: 0,
    refetchOnWindowFocus: false,
    initialPageParam: 0,
    queryKey: [QueryKeyEnum.LICENSE_PLATE, filters, sentTo, start, end],
    enabled,
    queryFn: async ({pageParam: nextId}) => {
      if (nextId === -1) throw new Error('No more pages to fetch')

      const response = await api.events.getAllLPR({
        batchSize: BATCH_SIZE,
        startId: nextId === 0 ? undefined : nextId,
        filter: filters,
        sentToHarpia: sentTo?.sentToHarpia ?? undefined,
        sentToSpia: sentTo?.sentToSpia ?? undefined,
        sentToSivana: sentTo?.sentToSivana ?? undefined,
        start: start,
        end: end,
        orderBy: {createdAt: 'DESC'},
      })

      return response
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage && allPages && allPages.some((c) => c && c.data.data.items.some((detection) => detection.id === lastPage.data.data.nextId))) {
        return null
      }

      return lastPage ? lastPage.data.data.nextId : undefined
    },
    throwOnError: () => {
      return false
    },
  })

  const getLprData = (lprEvents: Lpr[]) => {
    return lprEvents
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .filter((event, index, self) => index === self.findIndex((t) => t.id === event.id))
  }

  const disconnectWebsocket = () => {
    websocket.forceDisconnect()
    setLprEvents([])
  }

  return {
    ...query,
    websocket,
    forceDisconnect: disconnectWebsocket,
    data: getLprData([...lprEvents, ...(query.data?.pages.flatMap((page) => page.data.data.items) ?? [])]),
    lprEvents,
  }
}
